import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { ArrowLeft, ZoomIn, Play } from 'lucide-react';
import Lightbox from '../components/Lightbox';
import CTASection from '../components/CTASection';
import RelatedContent from '../components/RelatedContent';
import useSEO from '../hooks/useSEO';

const CATEGORY_LABEL = {
    'dry-ice': 'Dry Ice Blasting',
    'laser': 'Laser Cleaning',
    'sandblasting': 'Sandblasting',
    'pressure-washing': 'Pressure Washing',
    'mixed': 'Mixed Services',
};

const CATEGORY_TO_SERVICE_ANCHOR = {
    'dry-ice': '/services#dry-ice',
    'laser': '/services#laser',
    'sandblasting': '/services#sandblasting',
    'pressure-washing': '/services#pressure-washing',
};

// Convert youtube/vimeo watch links into embed URLs; leave uploaded/mp4 files untouched
const toEmbed = (url) => {
    if (!url) return '';
    // YouTube watch URL
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    // Vimeo
    const vmMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vmMatch) return `https://player.vimeo.com/video/${vmMatch[1]}`;
    return url;
};

const isDirectVideoFile = (url) => /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url || '');

const ProjectDetail = () => {
    const { key } = useParams();
    const [p, setP] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [lightboxIdx, setLightboxIdx] = useState(-1);
    const [relatedProjects, setRelatedProjects] = useState([]);
    const [recommendedMachines, setRecommendedMachines] = useState([]);

    useEffect(() => {
        api.get(`/projects/${key}`)
            .then((r) => setP(r.data))
            .catch(() => setNotFound(true));
    }, [key]);

    // Load related projects (same category) and recommended machines
    useEffect(() => {
        if (!p) return;
        api.get('/projects').then((r) => {
            const others = (r.data || []).filter((x) => (x.slug || x.id) !== (p.slug || p.id));
            const sameCat = others.filter((x) => x.category === p.category);
            setRelatedProjects((sameCat.length ? sameCat : others).slice(0, 3));
        }).catch(() => setRelatedProjects([]));
        api.get('/machines', { params: { category: p.category } }).then((r) => {
            setRecommendedMachines((r.data || []).slice(0, 3));
        }).catch(() => setRecommendedMachines([]));
    }, [p]);

    useSEO(p ? {
        title: `${p.title} — ${CATEGORY_LABEL[p.category] || 'Industrial Cleaning'} Case Study`,
        description: (p.summary || p.description || `Industrial cleaning case study by Dry Ice Bulls — ${p.title}.`).slice(0, 160),
        image: p.cover_image || (p.images && p.images[0]) || '',
        type: 'article',
    } : {});

    // Inject BreadcrumbList JSON-LD
    useEffect(() => {
        if (!p) return undefined;
        const scriptId = 'project-breadcrumb-jsonld';
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const data = {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
                { '@type': 'ListItem', position: 2, name: 'Projects', item: `${origin}/projects` },
                { '@type': 'ListItem', position: 3, name: p.title, item: `${origin}/projects/${p.slug || p.id}` },
            ],
        };
        let el = document.getElementById(scriptId);
        if (!el) {
            el = document.createElement('script');
            el.id = scriptId;
            el.type = 'application/ld+json';
            document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(data);
        return () => { const node = document.getElementById(scriptId); if (node) node.remove(); };
    }, [p]);

    if (notFound) {
        return (
            <div className="container-page pt-32 pb-24" data-testid="project-not-found">
                <div className="label-caps text-brand-primary mb-4">/ 404</div>
                <h1 className="font-display font-black uppercase text-4xl">Project not found</h1>
                <Link to="/projects" className="mt-6 inline-flex items-center gap-2 label-caps text-brand-primary">
                    <ArrowLeft className="h-4 w-4" /> Back to Project Gallery
                </Link>
            </div>
        );
    }
    if (!p) {
        return <div className="container-page pt-32 pb-24 text-white/40 label-caps text-sm">Loading…</div>;
    }

    const images = [p.cover_image, ...(p.images || [])].filter(Boolean);
    const videos = (p.videos || []).filter(Boolean);

    return (
        <div className="pt-24 pb-24" data-testid="project-detail-page">
            <div className="container-page">
                <Link to="/projects" className="inline-flex items-center gap-2 label-caps text-white/60 hover:text-white transition-colors text-sm">
                    <ArrowLeft className="h-4 w-4" /> Back to Project Gallery
                </Link>

                <div className="mt-6 flex items-center gap-3">
                    <span className="label-caps text-xs bg-brand-primary text-brand-ink px-3 py-1 font-bold">
                        {CATEGORY_LABEL[p.category] || p.category}
                    </span>
                </div>

                <h1 className="mt-4 font-display font-black uppercase text-4xl md:text-6xl leading-[0.98]">
                    {p.title}
                </h1>

                {p.summary && (
                    <p className="mt-6 max-w-3xl text-base md:text-lg text-white/80 leading-relaxed">{p.summary}</p>
                )}

                {/* Hero image (or first video if no images) */}
                {images.length > 0 && (
                    <button
                        type="button"
                        onClick={() => setLightboxIdx(0)}
                        className="group relative mt-10 w-full aspect-[16/9] border border-white/10 bg-white/[0.03] overflow-hidden flex items-center justify-center cursor-zoom-in"
                        data-testid="project-hero"
                        aria-label="Zoom image"
                    >
                        <img src={images[0]} alt={p.title} fetchPriority="high" decoding="async" className="w-full h-full object-contain" />
                        <span className="absolute top-4 right-4 flex items-center gap-1.5 label-caps text-white/80 bg-black/50 backdrop-blur-sm border border-white/20 px-2.5 py-1.5 text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <ZoomIn className="h-3.5 w-3.5" /> Click to zoom
                        </span>
                    </button>
                )}

                {/* Description */}
                {p.description && (
                    <section className="mt-14 max-w-3xl">
                        <div className="label-caps text-brand-primary mb-4">/ Project details</div>
                        <div className="text-white/80 leading-relaxed whitespace-pre-line text-base md:text-lg">
                            {p.description}
                        </div>
                    </section>
                )}

                {/* Image grid (excluding hero, which shown above) */}
                {images.length > 1 && (
                    <section className="mt-16">
                        <div className="label-caps text-brand-primary mb-4">/ Gallery</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {images.slice(1).map((img, i) => (
                                <button
                                    key={i}
                                    onClick={() => setLightboxIdx(i + 1)}
                                    className="aspect-square border border-white/10 bg-white/[0.03] overflow-hidden hover:border-brand-primary/50 transition-colors cursor-zoom-in"
                                    data-testid={`project-image-${i}`}
                                >
                                    <img src={img} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {/* Videos */}
                {videos.length > 0 && (
                    <section className="mt-16">
                        <div className="label-caps text-brand-primary mb-4 flex items-center gap-2">
                            <Play className="h-3.5 w-3.5" /> / Video walkthrough{videos.length > 1 ? 's' : ''}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {videos.map((v, i) => (
                                <div key={i} className="aspect-video border border-white/10 bg-black overflow-hidden" data-testid={`project-video-${i}`}>
                                    {isDirectVideoFile(v) ? (
                                        <video src={v} controls className="w-full h-full object-contain" />
                                    ) : (
                                        <iframe
                                            src={toEmbed(v)}
                                            className="w-full h-full"
                                            title={`${p.title} — video ${i + 1}`}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Service used + Equipment used — cross-links to boost internal linking */}
                <section className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl" data-testid="project-crosslinks">
                    <div className="border border-neutral-200 bg-neutral-50 p-6">
                        <div className="label-caps text-brand-primary text-xs mb-2">/ Service used</div>
                        <div className="font-display font-bold uppercase text-xl text-neutral-900 mb-3">
                            {CATEGORY_LABEL[p.category] || p.category}
                        </div>
                        <Link
                            to={CATEGORY_TO_SERVICE_ANCHOR[p.category] || '/services'}
                            className="inline-flex items-center gap-1.5 label-caps text-brand-primary hover:text-brand-primaryHover text-xs"
                            data-testid="project-service-link"
                        >
                            Learn about this service →
                        </Link>
                    </div>
                    {recommendedMachines.length > 0 && (
                        <div className="border border-neutral-200 bg-neutral-50 p-6">
                            <div className="label-caps text-brand-primary text-xs mb-2">/ Equipment used</div>
                            <ul className="space-y-1.5">
                                {recommendedMachines.map((m) => (
                                    <li key={m.id}>
                                        <Link
                                            to={`/machines/${m.slug || m.id}`}
                                            className="text-neutral-800 hover:text-brand-primary transition-colors text-sm"
                                            data-testid={`project-equipment-${m.slug || m.id}`}
                                        >
                                            → {m.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>
            </div>

            {/* Similar projects */}
            {relatedProjects.length > 0 && (
                <RelatedContent
                    eyebrow="/ Similar case studies"
                    title="More projects like this"
                    items={relatedProjects}
                    kind="project"
                    testid="project-related"
                />
            )}

            {/* Conversion CTA */}
            <CTASection
                variant="project"
                heading="Need similar results?"
                subheading="Request an industrial assessment — we'll evaluate your equipment, contamination, and downtime constraints, then recommend the right cleaning approach."
                context={{ subject: `Similar to: ${p.title}`, trackingLocation: 'project_bottom' }}
                testid="project-cta-bottom"
            />

            {lightboxIdx >= 0 && images.length > 0 && (
                <Lightbox
                    images={images}
                    index={lightboxIdx}
                    onClose={() => setLightboxIdx(-1)}
                    onIndex={(i) => setLightboxIdx(i)}
                />
            )}
        </div>
    );
};

export default ProjectDetail;
